import { useSelector } from 'react-redux';
import { useGetRBACAuthorityUser } from '../../querys/rbac/rbac.query';
import { RBACLevels, RBACActions, RBACPermissionTypesEnum } from '../../querys/rbac/rbac.query.model';

import { isEmpty } from 'lodash';
import asyncComponent from '../../helpers/AsyncFunc';
import { AppState } from '../../redux/redux.model';

export const routesByPath = {
  dashboard: asyncComponent(() => import('../dashboard')),
  ':companyId/characteristic-edit/:id?/by-phenomenon/:phenomenonId': asyncComponent(
    () => import('../Characteristic/characteristic.container')
  ),
  ':workspaceId/characteristic-edit/:id?/by-workspace/by-phenomenon/:phenomenonId': asyncComponent(
    () => import('../Characteristic/characteristic.container')
  ),
  ':companyId/characteristic-edit/:id?': asyncComponent(() => import('../Characteristic/characteristic.container')),
  'characteristic-edit/:id?/by-phenomenon/:phenomenonId': asyncComponent(() => import('../Characteristic/characteristic.container')),
  'characteristic-edit/:id?': asyncComponent(() => import('../Characteristic/characteristic.container')),
  phenomenons: asyncComponent(() => import('../Phenomenon/phenomenons')),
  'phenomenon-edit/:id?/by-company/:companyId': asyncComponent(() => import('../Phenomenon/PhenomenonEdit/phenomenonEdit')),
  'phenomenon-edit/:id?/by-workspace/:workspaceId': asyncComponent(() => import('../Phenomenon/PhenomenonEdit/phenomenonEdit')),
  'phenomenon-edit/:id': asyncComponent(() => import('../Phenomenon/PhenomenonEdit/phenomenonEdit')),
  'phenomenon-edit': asyncComponent(() => import('../Phenomenon/PhenomenonEdit/phenomenonEdit')),
  'multiple-phenomenons-indicator': asyncComponent(() => import('../MultiplePhenomenonsIndicator/multiplePhenomenonsIndicator')),
  ':companyId/indicator-edit/:id?/by-phenomenon/:phenomenonId': asyncComponent(() => import('../Indicator/indicatorEdit')),
  ':workspaceId/indicator-edit/:id?/by-workspace/by-phenomenon/:phenomenonId': asyncComponent(() => import('../Indicator/indicatorEdit')),
  ':workspaceId/indicator-edit/:id?/by-workspace': asyncComponent(() => import('../Indicator/indicatorEdit')),
  ':companyId/indicator-edit/:id?': asyncComponent(() => import('../Indicator/indicatorEdit')),
  'indicator-edit/:id?/by-phenomenon/:phenomenonId': asyncComponent(() => import('../Indicator/indicatorEdit')),
  'indicator-edit/:id?': asyncComponent(() => import('../Indicator/indicatorEdit')),
  'fixed-point-category-edit/:id?/by-company/:companyId': asyncComponent(() => import('../FixedPointCategories/fixedPointsCategoriesEdit')),
  'fixed-point-category-edit/:id?': asyncComponent(() => import('../FixedPointCategories/fixedPointsCategoriesEdit')),
  // Order matters: the workspace-scoped variant must be declared before the company-scoped fallback,
  // since both match URLs like `/<id>/independent-variable/<id>/by-workspace` and the first hit wins.
  ':workspaceId/independent-variable/:id?/by-workspace': asyncComponent(() => import('../IndependentVariable/independentVariableEdit')),
  ':companyId?/independent-variable/:id?': asyncComponent(() => import('../IndependentVariable/independentVariableEdit')),
  'fixed-point-category-edit': asyncComponent(() => import('../FixedPointCategories/fixedPointsCategoriesEdit')),
  'fixed-points-categories/:context': asyncComponent(() => import('../FixedPointCategories/fixedPointsCategories')),
  'fixed-points-categories/by-company/:companyId': asyncComponent(() => import('../FixedPointCategories/fixedPointsCategories')),
  'phenomenon-categories': asyncComponent(() => import('../PhenomenonCategory/phenomenonCategories')),
  'phenomenon-category-edit/:id': asyncComponent(() => import('../PhenomenonCategory/phenomenonCategoryEdit')),
  'phenomenon-category-edit': asyncComponent(() => import('../PhenomenonCategory/phenomenonCategoryEdit')),
  'new-methodology/:id': asyncComponent(() => import('../MethodologyContainer/methodologyContainer')),
  'v2/new-methodology/:id': asyncComponent(() => import('../MethodologyContainer/methodologyContainerV2')),
  templates: asyncComponent(() => import('../MethodologyTemplate/methodologyTemplate')),
  'new-methodologies/by-company/:companyId': asyncComponent(() => import('../MethodologyList/methodologyList')),
  methodologies: asyncComponent(() => import('../MethodologyMgt/methodologyMgt')),
  'users/rbac/:companyId': asyncComponent(() => import('../userV2RBAC/userV2RBAC')),
  organizations: asyncComponent(() => import('../Organizations/organizations')),
  'v2/organizations': asyncComponent(() => import('../Organizations/organizationsV2')),
  'properties/by-company/:companyId': asyncComponent(() => import('../Properties/Properties')),
  measurements: asyncComponent(() => import('../DataMaintenance/dataMaintenance')),
  'extra-dimensions/by-company/:companyId/inspection-group/:inspectionGroupId': asyncComponent(
    () => import('../ExtraDimensionsEdit/extraDimensionsEdit.component')
  ),
  'extra-dimensions/:id/by-company/:companyId/by-methodology/:methodologyId/inspection-group/:inspectionGroupId': asyncComponent(
    () => import('../ExtraDimensionsEdit/extraDimensionsEdit.component')
  ),
  'extra-dimensions/by-workspace/:workspaceId/inspection-group/:inspectionGroupId': asyncComponent(
    () => import('../ExtraDimensionsEdit/extraDimensionsEdit.component')
  ),
  'extra-dimensions/:id/by-workspace/:workspaceId/by-methodology/:methodologyId/inspection-group/:inspectionGroupId': asyncComponent(
    () => import('../ExtraDimensionsEdit/extraDimensionsEdit.component')
  ),
  'service-providers': asyncComponent(() => import('../ServiceProviders/serviceProviders')),
  'custom-fields': asyncComponent(() => import('../CustomFields/custom-fields')),
  schemas: asyncComponent(() => import('../Schemas/schemas')),
  'seeds-setup': asyncComponent(() => import('../YieldEstimationSetup/yieldEstimationSetup')),
  'seeds-setup/form': asyncComponent(() => import('../YieldEstimationSetup/yieldEstimationSetupForm')),
  // P40-61252: must stay ahead of the org route below - Switch takes the first match and the org
  // pattern would otherwise capture 'by-workspace' as a methodology id.
  'seeds-setup/form/by-workspace/:workspaceId/:methodologyId/:yieldEstimationConfigurationId?': asyncComponent(
    () => import('../YieldEstimationSetup/yieldEstimationSetupForm')
  ),
  'seeds-setup/form/:methodologyId/:organizationId/:yieldEstimationConfigurationId?': asyncComponent(
    () => import('../YieldEstimationSetup/yieldEstimationSetupForm')
  ),
  'schemas-new': asyncComponent(() => import('../Schemas/schemas-new')),
  'schemas/:workspaceId/:id': asyncComponent(() => import('../Schemas/schemas-new')),
  'schemas-clone/:workspaceId/:id': asyncComponent(() => import('../Schemas/schemas-new')),
  'ai-insights': asyncComponent(() => import('../AiInsightsBackoffice/AiInsightsBackoffice')),
  'ai-insights/prompts': asyncComponent(() => import('../AiInsightsBackoffice/AiInsightsBackoffice')),
  'ai-insights/prompts/new': asyncComponent(() => import('../AiInsightsBackoffice/pages/PromptTemplates/promptTemplateCreate')),
  'ai-insights/prompts/:id/edit': asyncComponent(() => import('../AiInsightsBackoffice/pages/PromptTemplates/promptTemplateEdit')),
  'digital-trap-thresholds': asyncComponent(() => import('../DigitalTrapThresholds/digitalTrapThresholds'))
};

export const rbacOperations = [
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.METHODOLOGY,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.FIXED_POINTS,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.CANONICAL_CROP,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.ADMIN_DEVICES,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.CANONICAL_PHENOMENON,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacLevels: RBACLevels.USER,
    rbacActions: RBACActions.CANONICAL_CHARACTERISTICS,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CANONICAL_INDICATORS,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CANONICAL_PHENOMENON_CATEGORY,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CANONICAL_MULTI_PHENOMENON_INDICATOR,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.ADMIN_ACCESS,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CUSTOM_CHARACTERISTICS,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CUSTOM_INDICATORS,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  },
  {
    rbacActions: RBACActions.CUSTOM_PHENOMENON,
    rbacLevels: RBACLevels.USER,
    rbacPermission: [RBACPermissionTypesEnum.READ, RBACPermissionTypesEnum.WRITE]
  }
];

export const usePagePermissions = () => {
  const pagePermission = useGetRBACAuthorityUser({
    rbacOperations
  });
  const flags = useSelector((state: AppState) => state.App.systemFlags);
  if (!pagePermission || isEmpty(pagePermission)) return [];

  const isOrganizationsV2Enabled = !!flags?.P40_59408_ENABLE_ORGANIZATIONS_V2;

  return [
    {
      path: 'dashboard',
      canAccessPermission: true
    },
    {
      path: ':workspaceId/characteristic-edit/:id?/by-workspace/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_CHARACTERISTICS]?.length
    },
    {
      path: ':companyId/characteristic-edit/:id?/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_CHARACTERISTICS]?.length
    },
    {
      path: ':companyId/characteristic-edit/:id?',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_CHARACTERISTICS]?.length
    },
    {
      path: 'characteristic-edit/:id?/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CHARACTERISTICS]?.length
    },
    {
      path: 'characteristic-edit/:id?',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CHARACTERISTICS]?.length
    },
    {
      path: 'phenomenons',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON]?.length
    },
    {
      path: 'phenomenon-edit/:id?/by-company/:companyId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_PHENOMENON]?.length
    },
    {
      path: 'phenomenon-edit/:id?/by-workspace/:workspaceId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_PHENOMENON]?.length
    },
    {
      path: 'phenomenon-edit/:id',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON]?.length
    },
    {
      path: 'phenomenon-edit',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON]?.includes(RBACPermissionTypesEnum.WRITE)
    },
    {
      path: 'multiple-phenomenons-indicator',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_MULTI_PHENOMENON_INDICATOR]?.length
    },
    {
      path: ':workspaceId/indicator-edit/:id?/by-workspace/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_INDICATORS]?.length
    },
    {
      path: ':companyId/indicator-edit/:id?/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_INDICATORS]?.length
    },
    {
      path: ':workspaceId/indicator-edit/:id?/by-workspace',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_INDICATORS]?.length
    },
    {
      path: ':companyId/indicator-edit/:id?',
      canAccessPermission: !!pagePermission?.[RBACActions.CUSTOM_INDICATORS]?.length
    },
    {
      path: 'indicator-edit/:id?/by-phenomenon/:phenomenonId',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_INDICATORS]?.length
    },
    {
      path: 'indicator-edit/:id?',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_INDICATORS]?.length
    },
    {
      path: 'fixed-point-category-edit/:id?/by-company/:companyId',
      canAccessPermission: !!pagePermission?.[RBACActions.FIXED_POINTS]?.includes(RBACPermissionTypesEnum.WRITE)
    },
    {
      path: 'fixed-point-category-edit/:id?',
      canAccessPermission: !!pagePermission?.[RBACActions.FIXED_POINTS]?.includes(RBACPermissionTypesEnum.WRITE)
    },
    // Order matters: keep aligned with routesByPath above so the workspace-scoped route is tried first.
    {
      path: ':workspaceId/independent-variable/:id?/by-workspace',
      canAccessPermission: true
    },
    {
      path: ':companyId?/independent-variable/:id?',
      canAccessPermission: true
    },
    {
      path: 'fixed-point-category-edit',
      canAccessPermission: !!pagePermission?.[RBACActions.FIXED_POINTS]?.includes(RBACPermissionTypesEnum.WRITE)
    },
    {
      path: 'fixed-points-categories/:context',
      canAccessPermission: !!pagePermission?.[RBACActions.FIXED_POINTS]?.length
    },
    {
      path: 'fixed-points-categories/by-company/:companyId',
      canAccessPermission: !!pagePermission?.[RBACActions.FIXED_POINTS]?.length
    },
    {
      path: 'phenomenon-categories',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON_CATEGORY]?.length
    },
    {
      path: 'phenomenon-category-edit/:id',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON_CATEGORY]?.length
    },
    {
      path: 'phenomenon-category-edit',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_PHENOMENON_CATEGORY]?.includes(RBACPermissionTypesEnum.WRITE)
    },
    {
      path: 'new-methodology/:id',
      canAccessPermission: !!pagePermission?.[RBACActions.METHODOLOGY]?.length
    },
    {
      path: 'v2/new-methodology/:id',
      canAccessPermission: !!pagePermission?.[RBACActions.METHODOLOGY]?.length
    },
    {
      path: 'templates',
      canAccessPermission: !!pagePermission?.[RBACActions.METHODOLOGY]?.length
    },
    {
      path: 'new-methodologies/by-company/:companyId',
      canAccessPermission: !!pagePermission?.[RBACActions.METHODOLOGY]?.length
    },
    {
      path: 'methodologies',
      canAccessPermission: !!pagePermission?.[RBACActions.METHODOLOGY]?.length
    },
    {
      path: 'users/legacy/:companyId',
      canAccessPermission: true
    },
    {
      path: 'users/rbac/:companyId',
      canAccessPermission: true
    },
    {
      path: 'organizations',
      canAccessPermission: !!pagePermission?.[RBACActions.ADMIN_DEVICES]?.length
    },
    {
      path: 'v2/organizations',
      canAccessPermission: isOrganizationsV2Enabled && !!pagePermission?.[RBACActions.ADMIN_DEVICES]?.length
    },
    {
      path: 'properties/by-company/:companyId',
      canAccessPermission: true
    },
    {
      path: 'measurements',
      canAccessPermission: !!pagePermission?.[RBACActions.ADMIN_ACCESS]?.length
    },
    {
      path: 'extra-dimensions/by-company/:companyId/inspection-group/:inspectionGroupId',
      canAccessPermission: true
    },
    {
      path: 'extra-dimensions/:id/by-company/:companyId/by-methodology/:methodologyId/inspection-group/:inspectionGroupId',
      canAccessPermission: true
    },
    {
      path: 'extra-dimensions/by-workspace/:workspaceId/inspection-group/:inspectionGroupId',
      canAccessPermission: true
    },
    {
      path: 'extra-dimensions/:id/by-workspace/:workspaceId/by-methodology/:methodologyId/inspection-group/:inspectionGroupId',
      canAccessPermission: true
    },
    {
      path: 'service-providers',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    },
    {
      path: 'seeds-setup',
      canAccessPermission: true
    },
    {
      path: 'seeds-setup/form',
      canAccessPermission: true
    },
    {
      path: 'seeds-setup/form/by-workspace/:workspaceId/:methodologyId/:yieldEstimationConfigurationId?',
      canAccessPermission: true
    },
    {
      path: 'seeds-setup/form/:methodologyId/:organizationId/:yieldEstimationConfigurationId?',
      canAccessPermission: true
    },
    {
      path: 'custom-fields',
      canAccessPermission: true
    },
    {
      path: 'schemas',
      canAccessPermission: true
    },
    {
      path: 'schemas-new',
      canAccessPermission: true
    },
    {
      path: 'schemas/:workspaceId/:id',
      canAccessPermission: true
    },
    {
      path: 'schemas-clone/:workspaceId/:id',
      canAccessPermission: true
    },
    {
      path: 'ai-insights',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    },
    {
      path: 'ai-insights/prompts',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    },
    {
      path: 'ai-insights/prompts/new',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    },
    {
      path: 'ai-insights/prompts/:id/edit',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    },
    {
      path: 'digital-trap-thresholds',
      canAccessPermission: !!pagePermission?.[RBACActions.CANONICAL_CROP]?.length
    }
  ];
};
